import { formatUtcDate, parseIsoDate, utcDayDiff } from "@/utils/common.utils";
import { formatInspectionWindow, formatMoney, resolveRole, roleLabel } from "@/utils/deals.utils";
import { resolveTaskOwnership } from "@/utils/task-ownership.utils";
import { DealPhase, DealRole, DealType } from "@/enum/common.enum";

type AnyRecord = Record<string, any>;

export function formatActivityTimestamp(value: unknown): string {
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return "Date unavailable";
  return date.toLocaleString(undefined, {
    month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
  });
}

export function formatMessageDate(value: unknown): string {
  const timestamp = new Date(String(value)).getTime();
  if (!Number.isFinite(timestamp)) return "Recently";
  const days = Math.max(0, Math.floor((Date.now() - timestamp) / 86_400_000));
  if (days === 0) return "Today";
  if (days === 1) return "1d ago";
  if (days < 7) return `${days}d ago`;
  return new Date(String(value)).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function formatScheduledAt(value: unknown): string {
  if (!value) return "";
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return "";
  const datePart = date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  if (date.getUTCHours() === 0 && date.getUTCMinutes() === 0) return datePart;
  const timePart = date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `${datePart} · ${timePart}`;
}

export function participantRoleLabel(participant: AnyRecord, deal: AnyRecord): string {
  if (participant.role === DealRole.AGENT || participant.role === DealRole.CO_AGENT) return "Agent";
  const resolved = resolveRole({
    role: participant.role, side: participant.side,
    isInternalHost: participant.role === DealRole.AGENT, hostDealType: deal.deal_type,
  });
  return roleLabel(resolved || participant.role);
}

export function getOpeningLine({ dealType, phase, firstName, agentName }: AnyRecord): string {
  const from = agentName ? ` from ${agentName}` : "";
  if (phase === DealPhase.CLOSED) {
    return dealType === DealType.BUY
      ? `Hi ${firstName} — welcome home. Everything's here for your records.`
      : `Hi ${firstName} — the sale is done. Everything's here for your records.`;
  }
  if (phase === DealPhase.UNDER_CONTRACT) {
    return `Hi ${firstName}, here's a quick update${from}. We're on the path to closing — I'll flag anything that needs your attention.`;
  }
  return `Hi ${firstName}, here's where things stand${from}.`;
}

export function getPresentJourneyTitle(phase: string): string {
  if (phase === DealPhase.UNDER_CONTRACT) return "On the path to closing";
  if (phase === DealPhase.LISTING) return "On the market";
  if (phase === DealPhase.PRE_LISTING) return "Getting ready to list";
  if (phase === DealPhase.PRE_APPROVAL) return "Getting your financing squared away";
  if (phase === DealPhase.HOUSE_SHOPPING) return "Looking at homes";
  return "Making progress";
}

function byChecklistOrder(a: AnyRecord, b: AnyRecord): number {
  return (a.sort_order ?? 0) - (b.sort_order ?? 0)
    || String(a.created_at || "").localeCompare(String(b.created_at || ""));
}

/**
 * The next few unmarked items from the agent's master checklist.
 *
 * The brief said "mirror the next 3 unmarked items from the Agent Master
 * Checklist", and that is literally what this is: callers pass the client's
 * whole task feed, not just the rows assigned to them. `seeds.py::_visibility_for`
 * marks every non-internal template row `client_visible`, so the feed the server
 * hands a client already *is* the master checklist minus the rows the agent
 * marked internal. Nothing is widened here — filtering to `assigned_role ===
 * "client"` was what made it a personal to-do list rather than a journey.
 *
 * Ordered by `sort_order`, which is the authoritative display sequence the
 * under-contract template establishes; `created_at` only breaks ties. The one
 * exception is an open gated item that has just become client-visible, either
 * by authorized confirmation or explicit agent release. That action is a
 * promise that the client can see the item now, so newly visible items are
 * pinned ahead of the three-item journey preview (newest first).
 */
export function nextClientJourneyItems(tasks: AnyRecord[], limit = 3): AnyRecord[] {
  const pending = (tasks || [])
    .filter((task) => task.status !== "complete")
    .sort(byChecklistOrder);

  const releaseStamp = (task: AnyRecord) => {
    if (task.client_release === "on_approval") return task.released_at;
    if (task.client_release === "after_confirmed") return task.confirmed_at;
    return null;
  };
  const newlyVisible = pending
    .filter((task) => releaseStamp(task))
    .sort((a, b) => String(releaseStamp(b)).localeCompare(String(releaseStamp(a))));
  const gatedItems = new Set(newlyVisible);
  const ordinary = pending.filter((task) => !gatedItems.has(task));

  // Keep the normal preview compact, but never hide an item the agent has just
  // told the client was sent. If more than `limit` such items remain open, all
  // of them are part of the visible journey until they are completed.
  return [...newlyVisible, ...ordinary]
    .slice(0, Math.max(limit, newlyVisible.length));
}

/**
 * The checklist items most recently ticked off — the "previous steps" half of
 * the journey.
 *
 * Ordered by `sort_order` rather than `completed_at` so the trail reads in the
 * same sequence as the items still ahead of it. Agents work the list roughly in
 * order but not strictly, and sorting the tail by completion time would show a
 * client three steps that appear to run backwards.
 */
export function recentCompletedItems(tasks: AnyRecord[], limit = 3): AnyRecord[] {
  return (tasks || [])
    .filter((task) => task.status === "complete")
    .sort(byChecklistOrder)
    .slice(-limit);
}

/** Role wording, keyed by who is reading it. Every portal (client, lender,
 *  attorney, co-agent, vendor) reuses `resolveTaskOwner`/`whoseTurnNow`, so the
 *  wording has to be relative to the viewer — a lender is "you" to themselves
 *  and "the lender" to everyone else, the same way "client" always means "you"
 *  when a client reads it. Falls back to the client's own map for any role not
 *  listed (there is no fifth portal today), and to a spaced-out role name below
 *  when even that map has no entry. */
const OWNER_ROLE_LABELS_BY_VIEWER: Record<string, Record<string, string>> = {
  client: {
    agent: "your agent", co_agent: "your agent", client: "you",
    lender: "your lender", attorney: "your closing attorney", vendor: "your vendor",
  },
  lender: {
    agent: "the agent", co_agent: "the agent", client: "the client",
    lender: "you", attorney: "the attorney", vendor: "the vendor",
  },
  attorney: {
    agent: "the agent", co_agent: "the agent", client: "the client",
    lender: "the lender", attorney: "you", vendor: "the vendor",
  },
  co_agent: {
    agent: "the listing agent", co_agent: "you", client: "the client",
    lender: "the lender", attorney: "the attorney", vendor: "the vendor",
  },
  vendor: {
    agent: "the agent", co_agent: "the agent", client: "the client",
    lender: "the lender", attorney: "the attorney", vendor: "you",
  },
};

export interface DealTeamMember {
  role: string;
  name: string;
  side?: string | null;
}

export interface TaskOwner {
  /** The assigned role, verbatim. */
  role: string;
  /** The person's name when the seat is filled, otherwise null. */
  name: string | null;
  /** "your lender" / "Dana Whitfield" — whichever the viewer can actually be told. */
  label: string;
  /** True when the client themselves owes this step — literal, not viewer-relative. */
  isClient: boolean;
  /** True when the *viewer's own* role owes this step. Equivalent to `isClient`
   *  when `viewerRole` is "client", the default; distinct for every other portal. */
  isSelf: boolean;
}

/**
 * Who owes a checklist item, named when we know the name.
 *
 * The server sends `team` with names only and scoped to the viewer's own side,
 * so this cannot address a client by the opposing side's attorney. When a seat
 * is unfilled — an attorney who has not accepted their invite yet — the role
 * wording stands in, which is why the label is resolved here rather than at the
 * call site: "Your closing attorney" is a true statement about an empty seat,
 * and a blank is not.
 *
 * `viewerRole` defaults to "client" so every existing call site keeps its exact
 * behavior; every other portal now passes its own role so "your turn" logic and
 * wording read correctly from a lender's or attorney's own seat.
 */
export function resolveTaskOwner(
  task: AnyRecord,
  team: DealTeamMember[] = [],
  deal: AnyRecord | null = null,
  viewerRole: string = "client",
): TaskOwner {
  const role = String(task?.assigned_role || "");
  const isClient = role === "client";
  const isSelf = role === viewerRole;
  // Delegated so the portal and the agent's own surfaces cannot disagree about
  // who owns a row. Only the *wording* is decided here — the client is told
  // "your lender", the agent is told "Lender", and both resolve to the same
  // person. Passing the deal is what lets the host agent be named at all: they
  // are deliberately absent from `team` (see `_client_team`), so a lookup
  // restricted to that list could only ever answer "your agent" for the person
  // whose name is sitting in the very same payload.
  const resolved = resolveTaskOwnership(task, deal || { team });
  // Never name the client or the viewer themselves — "you" (or "the client")
  // covers both, and `_client_team` excludes the client from `team` anyway, so
  // a name lookup for that row would come back empty regardless.
  // Keep the client-facing guard explicit: the client is never named back to
  // themselves. A non-client viewer gets the same treatment for their own
  // role through the viewer-relative `viewerName` below.
  const name = isClient ? null : resolved.name;
  const viewerName = isSelf ? null : name;
  const labelsForViewer = OWNER_ROLE_LABELS_BY_VIEWER[viewerRole] || OWNER_ROLE_LABELS_BY_VIEWER.client;
  const roleLabelText = labelsForViewer[role] || role.replace(/_/g, " ") || "the agent";
  return {
    role,
    name: viewerName || null,
    label: viewerName || roleLabelText,
    isClient,
    isSelf,
  };
}

/**
 * The open checklist items grouped by who owes them — the answer to "what is
 * actually happening right now" when nothing is on the viewer's own plate.
 *
 * One row per owner, carrying that owner's nearest item, because a reader
 * wants to know who is holding the deal rather than to audit a queue. Order
 * follows `sort_order`, so the person up next is listed first.
 */
export function whoseTurnNow(
  tasks: AnyRecord[],
  team: DealTeamMember[] = [],
  limit = 3,
  deal: AnyRecord | null = null,
  viewerRole: string = "client",
): Array<{ owner: TaskOwner; task: AnyRecord }> {
  const seen = new Set<string>();
  const rows: Array<{ owner: TaskOwner; task: AnyRecord }> = [];
  for (const task of (tasks || []).filter((t) => t.status !== "complete").sort(byChecklistOrder)) {
    const owner = resolveTaskOwner(task, team, deal, viewerRole);
    if (owner.isSelf || (viewerRole === "client" && owner.isClient) || seen.has(owner.role)) continue;
    seen.add(owner.role);
    rows.push({ owner, task });
    if (rows.length >= limit) break;
  }
  return rows;
}

/**
 * A task's title as a portal shows it. Template titles read as finished
 * outcomes ("Your property details are gathered"), so while a row is open it
 * reads as the server's action phrasing ("Gather your property details").
 */
export function taskLabel(task: AnyRecord | null | undefined): string {
  if (!task) return "";
  return (task.status !== "complete" && task.ask_title) || task.title || "";
}

/**
 * What the earnest-money row asks of the buyer, in plain words, from the
 * contract terms: "Please deliver your $10,000 earnest money deposit to
 * Meridian Title Co. by Oct 11." The row's own title ("Earnest money has been
 * delivered") is the owner's checklist copy and reads as a done state, which
 * on a "Your turn" card left clients unsure what to do (client feedback, Oct
 * 2026). Null for every other row, so the card's usual sentence stands.
 */
export function earnestMoneyInstruction(task: AnyRecord | null, deal: AnyRecord | null): string | null {
  if (!task || !/^earnest money has been delivered$/i.test(String(task.title || "").trim())) return null;
  const terms = deal?.contract_terms || {};
  const amount = formatMoney(terms.earnest_money_deposit);
  const holder = String(terms.earnest_money_holder || "").trim();
  const due = task.due_date ? formatUtcDate(task.due_date) : "";
  return `Please deliver your ${amount ? `${amount} ` : ""}earnest money deposit`
    + (holder ? ` to ${holder}` : " to the escrow holder named in your contract")
    + (due ? ` by ${due}` : "")
    + ". Tick it off here once it's sent.";
}

interface DealJournalInput {
  /** Next unmarked checklist items — what is coming. */
  upcoming?: AnyRecord[];
  /** Recently ticked-off checklist items — what already happened. */
  completed?: AnyRecord[];
  /** "client" (default) keeps the agent's first-person voice to the client
   *  reading their own deal — "I'll flag anything you need to weigh in on".
   *  "external" swaps just the body copy to neutral third person for a lender,
   *  attorney, or co-agent reading the same timeline — the keys, dates, and
   *  `items` are identical either way, only the sentences addressed to "you"
   *  change. */
  voice?: "client" | "external";
}

export function buildDealJournal(deal: AnyRecord, input: DealJournalInput = {}): AnyRecord[] {
  const { upcoming = [], voice = "client" } = input;
  const external = voice === "external";
  const entries: AnyRecord[] = [];
  const now = new Date();
  const format = (date: Date) => date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const terms = deal.contract_terms || {};
  const hasContractEntry = Boolean(deal.under_contract_at);
  const hasDueDiligenceEntry = Boolean(terms.due_diligence_end_date);
  const hasInspectionEntry = Boolean(terms.inspection_date);

  // Checklist rows the journal already narrates in its own words are dropped
  // before anything is built. The contract and due-diligence entries below are
  // derived from `under_contract_at` and `due_diligence_end_date`, and the
  // template rows for those same two milestones would otherwise print as a
  // bullet directly underneath the paragraph describing them. Matched on the
  // template titles in `config.py`, which are fixed copy rather than user
  // input — a row an agent typed by hand is never silently hidden.
  const completed = (input.completed || []).filter((task) => {
    const title = String(task.title || "").toLowerCase();
    if (hasContractEntry && title.includes("contract is signed")) return false;
    if (hasDueDiligenceEntry && title.includes("due diligence")) return false;
    if (hasDueDiligenceEntry && title.includes("due-diligence")) return false;
    if (hasInspectionEntry && /inspections? (are|is) (scheduled|underway)/.test(title)) return false;
    return true;
  });

  if (hasContractEntry) entries.push({
    key: "uc", tone: "past", dateLabel: format(new Date(deal.under_contract_at)), title: "Contract signed",
    body: "Everyone signed and we officially went under contract. The countdown to closing starts here.",
  });
  // The trail behind "right now". Dated from the newest item that carries a
  // `completed_at`; rows ticked off before that field existed simply contribute
  // no date rather than borrowing today's, which would date a months-old
  // milestone to this morning.
  if (completed.length) {
    const stamps = completed
      .map((task) => parseIsoDate(task.completed_at))
      .filter((date): date is Date => Boolean(date));
    const latest = stamps.length ? new Date(Math.max(...stamps.map((d) => d.getTime()))) : null;
    entries.push({
      key: "done", tone: "past", dateLabel: latest ? format(latest) : "Done",
      title: completed.length === 1 ? "Recently completed" : `${completed.length} steps completed`,
      body: null, items: completed,
    });
  }
  // `parseIsoDate`, not a local parse-to-UTC: these dates are rendered by
  // `format` above, which is local. Anchoring them to UTC and then formatting
  // locally printed the day after the stored one for every user east of
  // Greenwich, one line below a countdown card showing the correct day.
  // The inspection the agent booked, from Contract Details: when, the arrival
  // window and who is coming. Never "present" — that tone replaces the
  // "right now" entry and would hide what is queued under it.
  const inspection = terms.inspection_date ? parseIsoDate(terms.inspection_date, true) : null;
  if (inspection) {
    const window = formatInspectionWindow(terms);
    const company = terms.inspection_company ? ` with ${terms.inspection_company}` : "";
    const upcomingInspection = inspection.getTime() + 86_400_000 > now.getTime();
    entries.push(upcomingInspection ? {
      key: "inspection", tone: "future", dateLabel: format(inspection), title: "Home inspection",
      body: `${external ? "The" : "Your"} inspection is scheduled${window ? ` ${window.startsWith("at ") || window.startsWith("by ") ? window : `between ${window.replace(" – ", " and ")}`}` : ""}${company}.`,
    } : {
      key: "inspection", tone: "past", dateLabel: format(inspection), title: "Home inspection",
      body: `The inspection took place${company}.`,
    });
  }
  const dueDiligence = terms.due_diligence_end_date ? parseIsoDate(terms.due_diligence_end_date, true) : null;
  const closing = terms.closing_date ? parseIsoDate(terms.closing_date, true) : null;
  if (dueDiligence) {
    const days = Math.ceil((dueDiligence.getTime() - now.getTime()) / 86_400_000);
    entries.push(dueDiligence < now ? {
      key: "dd", tone: "past", dateLabel: format(dueDiligence), title: "Due diligence complete",
      body: "The window to raise inspection issues has closed. Terms are locked from here.",
    } : {
      key: "dd", tone: days <= 3 ? "present" : "future", dateLabel: format(dueDiligence),
      title: days <= 3 ? `${days} day${days === 1 ? "" : "s"} left of due diligence` : "Due diligence closes",
      body: external
        ? "The window to review inspection findings and negotiate anything meaningful. The agent will flag anything the client needs to weigh in on."
        : "Our window to review inspection findings and negotiate anything meaningful. I'll flag anything you need to weigh in on.",
    });
  }
  if (!entries.some((entry) => entry.tone === "present")) {
    const past = entries.filter((entry) => entry.tone === "past");
    const future = entries.filter((entry) => entry.tone === "future");
    entries.splice(0, entries.length, ...past, {
      // No `dateLabel`: the renderer prints "Right now" for the present entry
      // and never reads it, so the old literal "This week" was a claim about
      // timing that nothing displayed and nothing computed.
      key: "now", tone: "present", title: getPresentJourneyTitle(deal.phase),
      body: upcoming.length
        ? null
        : external
          ? "Nothing urgent on the client's side right now."
          : "Nothing urgent on your side — I'll write again the moment something needs your attention.",
      // Rendered as a checklist under the entry. Empty means the old reassuring
      // sentence still shows, which is the right answer when there is genuinely
      // nothing queued.
      items: upcoming,
    }, ...future);
  }
  if (closing) entries.push({
    key: "close", tone: closing < now ? "past" : "future", dateLabel: format(closing),
    title: closing < now ? "Closed" : "Closing day",
    body: closing < now
      ? (external ? "The sale finalized." : "The sale finalized. Everything's here for your records.")
      : terms.closing_address
        ? (external
          ? `The team will meet at ${terms.closing_address.split(",")[0]} to sign and hand over the keys.`
          : `We'll meet at ${terms.closing_address.split(",")[0]} to sign and hand over the keys.`)
        : (external
          ? "The client will sign the paperwork and the sale will finalize. Exact time and place once title confirms."
          : "You'll sign the paperwork and the sale will finalize. I'll send exact time and place once title confirms."),
  });
  return entries;
}

/**
 * The financing-contingency deadline, from whichever of the two inputs the
 * agent actually filled in.
 *
 * The contract-terms editor offers "Days from contract" and "Specific date" as
 * equal choices, and `dealSummaryDefaults` treats days as the mode whenever no
 * date is stored — but every consumer read only the date, so a contingency
 * entered as days silently had no deadline anywhere in the product.
 *
 * Shared by the hero countdown card and the command-center milestone strip so
 * the two cannot drift apart again.
 */
export function resolveFinancingContingencyDate(deal: AnyRecord): Date | null {
  // "when applicable" — a cash deal has no loan to be contingent on, so the
  // card is absent rather than empty. Checked before either input is read.
  if (!deal || deal.financing === "cash") return null;
  const terms = deal.contract_terms || {};

  const explicit = parseIsoDate(terms.financing_contingency_date, /* endOfDay */ true);
  if (explicit) return explicit;

  const days = Number(terms.financing_contingency_days);
  if (!Number.isFinite(days) || days <= 0) return null;
  // Days are meaningless without the contract date they count from. Falling
  // back to today would invent a deadline that moves every time you look at it.
  const anchor = parseIsoDate(deal.under_contract_at);
  if (!anchor) return null;

  const derived = new Date(anchor);
  derived.setDate(derived.getDate() + days);
  derived.setHours(23, 59, 59, 0);
  return derived;
}

export function buildClosingText(terms: AnyRecord): string | null {
  const bits: string[] = [];
  if (terms?.closing_address) bits.push(String(terms.closing_address).split(",")[0].trim());
  if (terms?.closing_contact_name) bits.push(`w/ ${terms.closing_contact_name}`);
  return bits.length ? bits.join(" · ") : null;
}

export function buildHeroCountdownCard({ target, now, anchor, fallbackWindowDays, ...rest }: AnyRecord): AnyRecord {
  const dayDiff = utcDayDiff(target, now) ?? 0;
  const totalSpanDays = anchor ? Math.max(fallbackWindowDays, Math.ceil((target - anchor) / 86_400_000)) : fallbackWindowDays;
  const elapsedDays = anchor ? Math.max(0, Math.floor((now - anchor) / 86_400_000)) : 0;
  const urgency = dayDiff < 0 ? "overdue" : dayDiff <= 3 ? "urgent" : dayDiff <= 7 ? "warning" : "calm";
  const primary = dayDiff < 0 ? Math.abs(dayDiff) : dayDiff === 0 ? "Today" : dayDiff;
  const unit = dayDiff < 0 ? (dayDiff === -1 ? "day past" : "days past")
    : dayDiff === 0 ? "" : dayDiff === 1 ? "day left" : "days left";
  return {
    ...rest, target, primary, unit, urgency,
    // Local render — `target` is already the user's local calendar day.
    secondary: target.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" }),
    ring: { fillPct: Math.min(100, (elapsedDays / Math.max(1, totalSpanDays)) * 100) },
  };
}

export function formatClientTaskDueDate(value: unknown): string {
  return formatUtcDate(value, { weekday: "short" });
}

// No `timeZone` override: `parseIsoDate` already produced the LOCAL calendar
// day the user's clock reads, so rendering it in UTC would shift the weekday
// back by one for anyone west of Greenwich.
export function formatCountdownWeekday(date: Date): string {
  return date.toLocaleDateString(undefined, {
    weekday: "short", month: "short", day: "numeric",
  });
}

export function formatCountdownWithTime(date: Date): string {
  const dateText = date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  if (date.getUTCHours() === 0 && date.getUTCMinutes() === 0) return dateText;
  const timeText = date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `${dateText} · ${timeText}`;
}

export function getCountdownChipText(row: AnyRecord): string {
  const { days, tone, target } = row;
  if (tone === "anchor") return formatCountdownWeekday(target);
  if (days < 0) {
    if (tone === "event") return `${formatCountdownWeekday(target)} · done`;
    // A due-diligence window ending in the past does NOT mean it was satisfied
    // — it may still be actively past due, so report the lapse rather than
    // claiming completion.
    if (tone === "dd") {
      const elapsed = Math.abs(days);
      return elapsed === 1 ? "Expired 1 day ago" : `Expired ${elapsed} days ago`;
    }
    return formatCountdownWeekday(target);
  }
  if (days === 0) return tone === "event" ? `Today · ${formatCountdownWithTime(target)}` : "Today";
  if (days === 1) return tone === "event" ? `Tomorrow · ${formatCountdownWithTime(target)}` : "Tomorrow";
  if (days <= 6) return tone === "event" ? formatCountdownWithTime(target) : formatCountdownWeekday(target);
  return `${days} days · ${formatCountdownWeekday(target)}`;
}

export function cleanScheduledTaskLabel(title: unknown): string {
  if (!title) return "Event";
  return String(title)
    .replace(/\s+scheduled$/i, "")
    .replace(/^schedule\s+/i, "")
    .replace(/^./, (character) => character.toUpperCase());
}

export function getCountdownTestId(row: AnyRecord): string {
  if (row.key === "uc") return "countdown-uc";
  if (row.key === "dd") return "countdown-dd";
  if (row.key === "close") return "countdown-closing";
  return `countdown-event-${row.key.replace(/^t:/, "")}`;
}
